<template>
  <div class="data-grid-wrapper">
    <ag-grid-vue
      :class="gridClasses"
      :domLayout="domLayout"
      :rowClass="gridRowClasses"
      :rowClassRules="gridRowClassRules"
      :columnDefs="gridColumnDefs"
      :rowData="gridRowData"
      :defaultColDef="gridDefaultColDef"
      :rowSelection="gridRowSelection"
      :rowMultiSelectWithClick="true"
      :animateRows="true"
      :suppressDragLeaveHidesColumns="true"
      :suppressClickEdit="true"
      :stopEditingWhenCellsLoseFocus="true"
      :enableCellTextSelection="true"
      :ensureDomOrder="true"
      :suppressNoRowsOverlay="true"
      :popupParent="popupParent"
      @rowClicked="onRowClicked"
      @cell-clicked="cellWasClicked"
      @selection-changed="onSelectionChanged"
      @grid-ready="onGridReady"
      @model-updated="onModelUpdated"
    >
    </ag-grid-vue>
    <!-- Empty state rendered by Vue instead of AG Grid's no-rows overlay:
         a plain overlay div is reactive (consumers swap the message via the
         slot), always positioned the same, and uses the app font instead of
         the grid theme's. Headers stay visible and clickable underneath. -->
    <div v-if="isGridEmpty" class="data-grid-empty-overlay">
      <slot name="empty">
        <i class="mdi mdi-file-search-outline text-4xl mb-2"></i>
        <span class="font-medium">No results found</span>
        <span class="text-sm mt-1">Try adjusting your search criteria</span>
      </slot>
    </div>
  </div>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue3'; // the AG Grid Vue Component
import { ref, watch, onMounted } from 'vue';

import 'ag-grid-community/styles/ag-grid.css'; // Core grid CSS, always needed
import 'ag-grid-community/styles/ag-theme-alpine.css'; // Optional theme CSS

import { computed } from '@vue/reactivity';

export default {
  name: 'App',
  components: {
    AgGridVue,
  },
  emits: ['cellClicked', 'rowSelectionChange', 'rowClicked'],
  props: {
    columnsDefs: {
      type: Array,
      required: true,
    },
    rowsData: {
      type: Array,
      required: true,
    },
    class: {
      type: String,
      default: '',
    },
    rowClassRules: {
      type: Object,
      default: () => ({}),
    },
    defaultColDef: {
      type: Object,
      default: () => ({}),
    },
    rowSelection: {
      type: String,
      default: '',
    },
    domLayout: {
      type: String,
      default: '',
    },
    // AG Grid quick filter: free-text match against every visible column.
    quickFilterText: {
      type: String,
      default: '',
    },
  },
  setup(props, context) {
    const gridClasses = computed(() => {
      return 'ag-theme-alpine ' + props.class;
    });

    const gridRowClasses = computed(() => {
      return props.rowClass;
    });

    const gridRowClassRules = computed(() => {
      return {
        warning: (params) => {
          let partNumberIsValid = true;
          if (params?.data?.isValid !== undefined) {
            partNumberIsValid = params.data.isValid;
          }
          return !partNumberIsValid;
        },
        ...props.rowClassRules,
      };
    });

    // Each Column Definition results in one Column.
    const gridColumnDefs = computed(() => {
      return props.columnsDefs;
    });

    // Set rowData to Array of Objects, one Object per Row
    const gridRowData = computed(() => {
      return props.rowsData;
    });

    // DefaultColDef sets props common to all Columns
    const gridDefaultColDef = computed(() => {
      return {
        sortable: true,
        filter: true,
        flex: 1,
        resizable: true,
        lockPinned: true,
        ...props.defaultColDef,
      };
    });

    const gridRowSelection = computed(() => {
      return props.rowSelection;
    });

    const gridApi = ref(null); // Optional - for accessing Grid's API

    // Quick filter is applied through the grid API directly instead of the
    // ag-grid-vue3 `quickFilterText` property binding — the wrapper's
    // generated watchers proved unreliable at runtime, while an explicit
    // setQuickFilter call is deterministic.
    const applyQuickFilter = () => {
      if (gridApi.value) {
        gridApi.value.setQuickFilter(props.quickFilterText || '');
      }
    };
    watch(() => props.quickFilterText, applyQuickFilter);

    // Obtain API from grid's onGridReady event
    const onGridReady = (params) => {
      gridApi.value = params.api;
      // The grid often mounts after the user already typed (it renders only
      // once data arrives), so apply any pending filter right away.
      applyQuickFilter();
      isGridEmpty.value = params.api.getDisplayedRowCount() === 0;
    };

    const cellWasClicked = (event) => {
      context.emit('cellClicked', event);
    };

    const onSelectionChanged = (event) => {
      context.emit('rowSelectionChange', event);
    };

    const onRowClicked = (event) => {
      context.emit('rowClicked', event);
    };

    // Tracks "nothing displayed" from any cause — empty rowData or a filter
    // (e.g. the quick filter) removing every row — so the empty-state overlay
    // covers both and a search with no matches isn't a silent blank grid.
    const isGridEmpty = ref(false);
    const onModelUpdated = (event) => {
      isGridEmpty.value = event.api.getDisplayedRowCount() === 0;
    };

    const popupParent = ref(null);

    onMounted(() => {
      // Set popupParent to the grid's parent element
      popupParent.value = document.querySelector('.ag-theme-alpine');
    });

    return {
      gridRowSelection,
      gridClasses,
      gridRowClasses,
      gridRowClassRules,
      onGridReady,
      gridColumnDefs,
      gridRowData,
      gridDefaultColDef,
      cellWasClicked,
      onSelectionChanged,
      onRowClicked,
      onModelUpdated,
      isGridEmpty,
      popupParent,
    };
  },
};
</script>

<style lang="scss">
.ag-theme-alpine {
  width: 100%;
  height: 100%;
}

// Consumers' fallthrough class/style (e.g. height) land on this wrapper;
// the grid inside fills it via the .ag-theme-alpine 100% sizing above.
.data-grid-wrapper {
  position: relative;
}

// Centered in the body area (padding clears the 48px Alpine header row).
// Font is pinned to the app's: the wrapper usually carries .ag-theme-alpine,
// whose 13px system-stack font would otherwise leak in. pointer-events: none
// keeps the headers underneath sortable.
.data-grid-empty-overlay {
  position: absolute;
  inset: 0;
  padding-top: 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  color: #9ca3af;
  font-family: 'Archivo', sans-serif;
  font-size: 1rem;
}
</style>
